import React from "react";
import { Link } from "react-router-dom";
import "./HerlevSeo.css";
import Seo from "../../../Seo/Seo";
import {
    webPageSchema,
    faqPageSchema,
    serviceSchema,
} from "../../../Seo/jsonld";
import { getRouteSeo } from "../../../Seo/routeSeo";

// Replaced a bare <Helmet> that only set title/description/og:title. <Seo />
// additionally emits canonical, robots, the full Open Graph and Twitter sets,
// and the structured data below — all from the central route registry.
const PATH = "/skolemad-ballerup";


const BALLERUP_FAQS = [
    [
        "Tilbyder I skolemad til alle skoler i Ballerup?",
        "Ja, vi tilbyder skolemad til grundskoler, private skoler, friskoler og andre uddannelsesinstitutioner i Ballerup. Du kan kontakte os uanset skoletype eller elevantal. Vi skaber en løsning, der passer til din institution.",
    ],
    [
        "Hvordan fungerer bestilling af skolemad i Ballerup?",
        "Du kan bestille gennem vores onlineplatform eller ved at kontakte os direkte. Når I har valgt menuer og startdato, håndterer vi resten. Bestillinger kan normalt ændres med kort varsel, så du har fleksibilitet.",
    ],
    [
        "Kan virksomheder i Ballerup få frokostordning?",
        "Ja, virksomheder og kontorer i Ballerup kan få daglig frokost leveret. Vi tilbyder forskellige menuer, og du bestemmer, hvor ofte leveringen skal ske. Det kan være hver dag, nogle gange om ugen eller efter behov.",
    ],
    [
        "Tilbyder I catering til arrangementer i Ballerup?",
        "Ja, vi leverer catering til møder, fester, seminarer og andre arrangementer i Ballerup. Du kan vælge menuer, der passer til dit event, og vi håndterer levering og opsætning.",
    ],
    [
        "Kan I tilpasse sig diætiske ønsker og allergier?",
        "Ja, vi tager diætiske ønsker og allergier meget seriøst. Når du bestiller, fortæller du os om eventuelle krav, og vi sikrer, at maden tilberedes derefter.",
    ],
    [
        "Hvor leverer I mad i Ballerup?",
        "Vi leverer til hele Ballerup kommune. Oplyse din adresse, så aftaler vi leveringstider og -sted med dig.",
    ],
    [
        "Hvordan er priserne for skolemad i Ballerup?",
        "Priserne afhænger af, hvad du ønsker – hvilken type mad, antal personer, leveringsfrekvens osv. Vi giver gerne et konkret tilbud, når vi kender dine behov. Kontakt os for at diskutere priser.",
    ],
    [
        "Kan I starte en madordning kort varsel?",
        "Det afhænger af, hvad du har brug for. Kontakt os, og vi gør vores bedste for at imødekomme dit ønske.",
    ],
    [
        "Hvordan administrerer skoler madordninger gennem jer?",
        "Du får adgang til vores platform, hvor du kan bestille og ændre dine ønsker. Vi håndterer tilberedning og levering. Det gør administrationen enkel for skolen.",
    ],
];

const SkolemadBallerup = () => {
    const menuItems = [
        ["Skoler får Pålidelig Daglig Skolemad", "Grundskoler, private skoler og friskoler i Ballerup kan få leveret fuldt planlagt skolemad. Vi tilpasser os skolens timeplan, antal elever og ønsker til menuer. Rektorer og skolekøkkener kan afgøre, om maden skal tilberedes på stedet eller leveres klar til servering."],
        ["Institutioner får Administrativ Lettelse", "Børnehaver, dagcentre, ældrecentre og andre institutioner kan delegere madordningen til os. Vi håndterer bestilling, levering og tilpasning til brugergruppe."],
        ["Virksomheder giver Medarbejderne en Bedre Dag", "Kontorer og arbejdspladser i Ballerup kan tilbyde daglig frokost uden selv at skulle styre det. Medarbejdere får noget godt at spise, og virksomheden slipper for administrationen."],
        ["Organisationer og Kommunale Tilbud Forenkles", "Foreninger, kommunale enheder og andre organisationer kan få deres forplejning ordnet uden småkræmeri."],
        ["Sådan Starter du", "Du kan kontakte os uanset hvor i Ballerup du er, eller hvilken type organisation du repræsenterer. Vi aftaler detaljer individuelt og skaber en løsning, der passer til dig."],
    ];

    const reasons = [
        ["Pålidelig Levering Hver Dag", "Skoler og institutioner i Ballerup kan stole på, at maden ankommer præcist, når den skal. Ingen forglemmelser, ingen forsinkelser – bare pålidelig service.", "fa-school"],
        ["Fokus på Kvalitet", "Vi vælger ingredienser omhyggeligt. Maden skal ikke bare mætte – den skal være sund, smagfuld og tilberedt med omsorg.", "fa-utensils"],
        ["Fleksibilitet, der Passer til Virkeligheden", "Vi ved, at skoler og institutioner ikke altid har faste behov. Menuer kan tilpasses, bestillinger kan ændres, og særlige ønsker kan imødekommes. Fleksibilitet er indbygget i vores service.", "fa-mobile-alt"],
        ["Enkel Bestilling og Administration", "Vores onlineplatform gør det simpelt at styre madordninger. Ingen unødige bureaukrati – bare direkte og effektiv bestilling.", "fa-leaf"],
        ["Ernæring i Fokus", "Uanset om det handler om børn eller voksne, er ernæring vigtig. Vi tager hensyn til balancerede måltider, allergier og diætiske ønsker.", "fa-map-marker-alt"],
    ];

    return (
        <>
            <Seo
                path={PATH}
                jsonLd={[
                    webPageSchema({
                        title: getRouteSeo(PATH)?.title,
                        description: getRouteSeo(PATH)?.description,
                        url: PATH,
                    }),
                    serviceSchema({
                        name: "Skolemad og madordninger i Ballerup",
                        description: getRouteSeo(PATH)?.description,
                        url: PATH,
                        areaServed: "Ballerup",
                    }),
                    faqPageSchema(
                        BALLERUP_FAQS.map(([question, answer]) => ({ question, answer }))
                    ),
                ]}
            />
            <main className="herlev-page remove-space">
                <section className="herlev-hero">
                    <div className="herlev-hero-shape herlev-hero-shape-one" />
                    <div className="herlev-hero-shape herlev-hero-shape-two" />
                    <div className="container">
                        <div className="row align-items-center">
                            <div className="col-lg-7">
                                <div className="herlev-hero-copy wow fadeInUp">
                                    <span className="herlev-kicker">Skolemad og Madordninger i Ballerup</span>
                                    <h1>Madordning i Ballerup – Skolemad, Frokostordning og Catering</h1>
                                    <div className="herlev-rule"><span /><span /></div>
                                    <p className="herlev-lead">
                                        Daglig madordning kræver planlægning, pålidelighed og kvalitet. Onlineskolemad leverer skolemad, frokostordninger og catering til skoler, institutioner, virksomheder og organisationer i Ballerup. Uanset om du administrerer en skole med 200 elever, en daginstitution, et kontor eller en kommune, har vi en løsning, der passer til dine behov.
                                    </p>
                                    <p className="herlev-lead">
                                        I Ballerup, hvor der er både børnefamilier, arbejdspladser og uddannelsesinstitutioner, er det vigtig at have en madleverandør, man kan stole på. Vi sikrer, at skoler, institutioner og virksomheder får den forplejning, de har brug for, uden besvær med planlægning og bestilling.

                                    </p>
                                    <div className="herlev-actions">
                                        {/* <Link className="btn btn-1 gap-1" to="/menu">Bestil skolemad <i className="fas fa-arrow-right" /></Link> */}
                                        <Link className="herlev-text-link" to="/contact">Kontakt os for en uforpligtende samtale <i className="fas fa-arrow-right" /></Link>
                                    </div>
                                    <div className="herlev-trust-row">
                                        <span><i className="fas fa-check-circle" /> Egne chauffører</span>
                                        <span><i className="fas fa-check-circle" /> Online bestilling</span>
                                        <span><i className="fas fa-check-circle" /> Halal</span>
                                    </div>
                                    <div className="flex-group play-app row">
                                        <div className="col-md-6 ">
                                            <Link className="btn btn-dark google_ply mb-3" target="blank" to={'https://apps.apple.com/dk/app/onlineskolemad/id6741146828?l=da'}>
                                                <span className="play_img">
                                                    <img src="/assets/img/apple-icon.svg" alt="Apple App Store" />
                                                </span>
                                                <div className="download-cont">
                                                    <span>Download i</span>
                                                    <p>Appstore</p>
                                                </div>
                                            </Link>
                                            <img className="qr-img" src="/assets/img/qr-ios.png" alt="QR-kode til Onlineskolemad i App Store" />
                                        </div>
                                        <div className="col-md-6 mt-md-0 mt-4 mb-md-0 mb-5">
                                            <Link className="btn btn-dark google_ply mb-3" target="blank" to={'http://play.google.com/store/apps/details?id=com.onlineskolemad&hl=en'}>
                                                <span className="play_img">
                                                    <img src="/assets/img/palystore-icon.svg" alt="Google Play" />
                                                </span>
                                                <div className="download-cont">
                                                    <span>Hent den i</span>
                                                    <p>Google Play</p>
                                                </div>
                                            </Link>
                                            <img className="qr-img" src="/assets/img/qr-android.png" alt="QR-kode til Onlineskolemad i Google Play" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-5">
                                <div className="herlev-hero-image wow fadeInRight">
                                    <img src="/assets/img/childrens-2.png" alt="Børn, der nyder skolemad" />
                                    <div className="herlev-hero-note"><strong>2730</strong><span>Ballerup</span></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="herlev-intro sec-pd">
                    <div className="container">
                        <div className="row align-items-center">
                            <div className="col-lg-6">
                                <div className="herlev-photo-frame wow fadeInLeft">
                                    <img src="/assets/img/food-1.png" alt="Frisk skolemad fra Onlineskolemad" />
                                    <span className="herlev-photo-stamp"><i className="fas fa-heart" /> Gør frokosten lettere</span>
                                </div>
                            </div>
                            <div className="col-lg-6">
                                <div className="herlev-copy-block wow fadeInUp">
                                    <span className="herlev-eyebrow">En enklere skoledag</span>
                                    <h2>Hvorfor Pålidelig Madordning Betyder Noget i Ballerup</h2>
                                    <div className="herlev-rule"><span /><span /></div>
                                    <p>
                                        Skoler, institutioner og virksomheder i Ballerup ved, at en velplanlagt madordning ikke kun handler om at mætte maver. Det handler om at:
                                    </p>
                                    <ul className="herlev-check-list">
                                        <li>Frigøre tid til lærere og personale, så de kan fokusere på det vigtigste</li>
                                        <li>Sikre, at børn og medarbejdere får ordentlig ernæring hver dag</li>
                                        <li>Forenkle administrative opgaver omkring bestilling og levering</li>
                                        <li>Gøre hverdagen mere forudsigelig for både børnefamilier og virksomheder</li>
                                    </ul>
                                    <p>
                                        Ballerup er et område med stærke uddannelsesinstitutioner og virksomheder, der har høje krav til kvalitet og pålidelighed. Onlineskolemad er udviklet netop for at møde disse krav. Vi tager os af madordningen, så du kan fokusere på alt det andet.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="sec-pd bg-gray position-relative">
                    <div className="container">
                        <div className="row align-items-center">
                            <div className="col-md-6 mmb-20">
                                <div>
                                    <div className="title-1 wow fadeInUp" data-wow-delay="0.1s">
                                        <h2>
                                            Skolemad og Forplejning i Ballerup – Et Lokalt Behov
                                        </h2>
                                        <div className="herlev-rule"><span /><span /></div>
                                        <p>
                                            Ballerup er hjemme for skoler på alle niveauer, fra børnehaver og grundskoler til ungdomsuddannelser. Hertil kommer adskillige institutioner, virksomheder og kommunale organisationer, der dagligt skal løse forplejningsopgaven for medarbejdere, brugere og gæster.
                                        </p>
                                        <p>
                                            I Ballerup er der en særlig mangfoldighed af institutionstyper:
                                        </p>
                                        <p>
                                            <strong> Uddannelsesinstitutioner, grundskoler,</strong> privatskoler, friskoler og ungdomsuddannelser har alle det tilfælles, at de skal sikre, at elever får adgang til pålidelig frokost hver dag. Mange skoler søger efter leverandører, der kan tilbyde fleksible ordninger, diætisk tilpasning og kvalitet.
                                        </p>
                                        <p>
                                            <strong> Familier i Ballerup </strong> Med tusindvis af børnefamilier i kommunen er der konstant behov for skolemad, som kan leveres direkte til skolerne. Forældre værdsætter, når de kan være sikre på, at deres børn får ordentlig mad – uden at det kræver ekstra planlægning.
                                        </p>
                                        <p>
                                            <strong>Virksomheder og Kontorer</strong> Ballerup har en dynamisk erhvervsliv med mange kontorer og arbejdspladser. Medarbejdere værdsætter en god frokostordning, og arbejdsgivere ser det som en vigtig del af arbejdsmiljøet.
                                        </p>
                                        <p>
                                            <strong>Institutioner og Organisationer</strong> Fra plejecentre til foreninger og kommunale tilbud – institutioner i Ballerup har brug for pålidelig forplejning, der kan tilpasses forskellige brugergrupper.
                                        </p>
                                        <p>
                                            <strong>Onlineskolemad forstår</strong>, at Ballerup er mere end bare en by – det er et sted med mangfoldige institutionelle behov og familier, der søger kvalitet, pålidelighed og fleksibilitet.
                                        </p>
                                        <div className="flex-group play-app row">
                                            <div className="col-md-6 mt-md-0 mt-4 mb-md-0 mb-5">
                                                <Link className="btn btn-dark google_ply mb-3" target="blank" to={'https://apps.apple.com/dk/app/onlineskolemad/id6741146828?l=da'}>
                                                    <span className="play_img">
                                                        <img src="/assets/img/apple-icon.svg" alt="Apple App Store" />
                                                    </span>
                                                    <div className="download-cont">
                                                        <span>Download i</span>
                                                        <p>Appstore</p>
                                                    </div>
                                                </Link>
                                                <img className="qr-img" src="/assets/img/qr-ios.png" alt="QR-kode til Onlineskolemad i App Store" />
                                            </div>
                                            <div className="col-md-6">
                                                <Link className="btn btn-dark google_ply mb-3" target="blank" to={'http://play.google.com/store/apps/details?id=com.onlineskolemad&hl=en'}>
                                                    <span className="play_img">
                                                        <img src="/assets/img/palystore-icon.svg" alt="Google Play" />
                                                    </span>
                                                    <div className="download-cont">
                                                        <span>Hent den i</span>
                                                        <p>Google Play</p>
                                                    </div>
                                                </Link>
                                                <img className="qr-img" src="/assets/img/qr-android.png" alt="QR-kode til Onlineskolemad i Google Play" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="img-style-3 pl-2per img-res-md wow fadeInRight">
                                    <img src="/assets/img/download-mobile.png" alt="Onlineskolemad-appen på en mobiltelefon" loading="lazy" />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="download-banner img-res-md">
                        <img src="/assets/img/download-app.png" alt="Download Onlineskolemad-appen" loading="lazy" />
                    </div>
                </section>

                <section className="herlev-pale-section sec-pd">
                    <div className="container">
                        <div className="herlev-section-heading">
                            <span className="herlev-eyebrow">Noget for enhver frokost</span>
                            <h2>Onlineskolemad i Ballerup – Sådan Støtter vi Lokalt</h2>
                            <p>Vi arbejder med skoler, institutioner, virksomheder og organisationer overalt i Ballerup. Vores leveringsomfang dækker hele kommunen, og vi har erfaring med at arbejde med forskellige institutionstyper.</p>
                        </div>
                        <div className="row">
                            {menuItems.map(([title, text], index) => (
                                <div className="col-md-6 col-lg-4" key={title}>
                                    <article className="herlev-menu-item wow fadeInUp" data-wow-delay={`${index * 0.08}s`}>
                                        <span className="herlev-menu-icon"><i className={`fas ${["fa-utensils", "fa-bread-slice", "fa-box", "fa-leaf", "fa-glass-whiskey", "fa-seedling"][index]}`} /></span>
                                        <div><h3>{title}</h3><p>{text}</p></div>
                                    </article>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="herlev-audience sec-pd">
                    <div className="container">
                        <div className="row align-items-end mb-4">
                            <div className="col-lg-8">
                                <span className="herlev-eyebrow">En løsning til hverdagen</span>
                                <h2>Vores Madordninger og Forplejningstilbud i Ballerup</h2>
                                <p className="herlev-heading-note">Fra den enkelte madpakke til en komplet madordning for skolen.</p>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-md-4">
                                <article className="herlev-audience-card">
                                    <span>01</span>
                                    <h3>Skolemad til Grundskoler og Uddannelsesinstitutioner</h3>
                                    <p>Daglig skolemad er vores speciale. Vi leverer varme eller kolde måltider direkte til skoler i Ballerup, tilpasset årets gang og elevernes ernæringsbehov.</p>
                                    <ul className="herlev-check-list">
                                        <li>Normale og diætiske menuer</li>
                                        <li>Mødt på den enkelte skoles tidsskema</li>
                                        <li>Bestilling via let platform</li>
                                        <li>Pålidelig levering hver dag</li>
                                    </ul>
                                    <Link to="/contact">vide mere <i className="fas fa-arrow-right" /></Link>
                                </article>
                            </div>
                            <div className="col-md-4">
                                <article className="herlev-audience-card">
                                    <span>02</span>
                                    <h3>Frokostordninger til Virksomheder og Kontorer</h3>
                                    <p>Virksomheder i Ballerup kan tilbyde deres medarbejdere daglig frokost uden selv at skulle administrere ordningen. Vi leverer friske og appetitlige måltider til kontoret, så medarbejderne kan holde en ordentlig pause.</p>
                                    <ul className="herlev-check-list">
                                        <li>Fleksible menuer, der kan tilpasses virksomhedskultur</li>
                                        <li>Levering til kontoradressen</li>
                                        <li>Enkel bestilling og fakturering</li>
                                        <li>Både varme og kolde retter</li>
                                    </ul>
                                    <Link to="/contact">vide mere <i className="fas fa-arrow-right" /></Link>
                                </article>
                            </div>
                            <div className="col-md-4">
                                <article className="herlev-audience-card">
                                    <span>03</span>
                                    <h3>Kantineordninger til Institutioner</h3>
                                    <p>Institutioner, plejecentre, ældrecentre, daginstitutioner, arbejdsværksteder, kan få styret deres kantineordning gennem Onlineskolemad. Vi sikrer varierede menuer og kvalitet.</p>
                                    <ul className="herlev-check-list">
                                        <li>Tilpasset institutionens brugere</li>
                                        <li>Ernæring i fokus</li>
                                        <li>Enkel bestilling</li>
                                    </ul>
                                    <Link to="/contact">vide mere <i className="fas fa-arrow-right" /></Link>
                                </article>
                            </div>
                            <div className="col-md-4">
                                <article className="herlev-audience-card">
                                    <span>04</span>
                                    <h3>Catering og Mad til Arrangementer</h3>
                                    <p>Når Ballerup-skoler, virksomheder eller organisationer skal arrangere events, fest eller seminarer, kan vi stille kulinarisk værktøj til rådighed.</p>
                                    <ul className="herlev-check-list">
                                        <li>Skræddersyede menuer til dit arrangement</li>
                                        <li>Levering og opsætning</li>
                                        <li>Fleksible løsninger fra mindre møder til større begivenheder</li>
                                    </ul>
                                    <Link to="/contact">vide mere <i className="fas fa-arrow-right" /></Link>
                                </article>
                            </div>
                            <div className="col-md-4">
                                <article className="herlev-audience-card">
                                    <span>05</span>
                                    <h3>Mad ud af Huset – Madlevering på Bestilling</h3>
                                    <p> Ud over faste ordninger kan skoler, institutioner og virksomheder også bestille mad til mindre arrangementer eller special-events.</p>
                                    <ul className="herlev-check-list">
                                        <li>Ad hoc-ordninger uden langdistancekontrakt</li>
                                        <li>Hurtig håndtering</li>
                                        <li>Kvalitet som altid</li>
                                    </ul>
                                    <Link to="/contact">vide mere <i className="fas fa-arrow-right" /></Link>
                                </article>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="herlev-steps sec-pd">
                    <div className="container">
                        <div className="row align-items-center">
                            <div className="col-lg-5">
                                <div className="herlev-copy-block">
                                    <h2>Sådan Kommer du i Gang med Madordning i Ballerup</h2>
                                    <div className="herlev-rule"><span /><span /></div>
                                    <p>Bestilling er bygget op omkring en enkel digital proces.</p>
                                    <Link className="btn btn-1" to="/signup">Opret bruger</Link>
                                </div>
                            </div>
                            <div className="col-lg-7">
                                <div className="herlev-step-list">
                                    {["Du Kontakter os", "Vi Laver et Tilbud", "Du Godkender Ordningen", "Vi Starter Leveringen", "Du Nyder Godt af Pålidelig Forplejning"].map((step, index) => (
                                        <div className="herlev-step" key={step}>
                                            <span>0{index + 1}</span>
                                            <div>
                                                <h3>{step}</h3>
                                                <p>
                                                    {[
                                                        "Ring, send en e-mail eller udfyld en formular. Fortæl os om dine behov – hvor mange personer, hvilken type mad, og hvad dine særlige ønsker er.",
                                                        "Vi skaber en løsning, der passer til dig. Det kan være fast ordning, fleksibel bestilling eller engangsordre.",
                                                        "Vi gennemgår menuer, priser og vilkår sammen. Du godkender, hvad der skal leveres.",
                                                        "Fra og med aftalt dato starter vi med at levere maden. Bestilling sker via vores platform eller telefonisk.",
                                                        "Hver dag får du den mad, du har bestilt – præcis på tiden, i den kvalitet, du forventer."
                                                    ][index]
                                                    }
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="herlev-audience sec-pd">
                    <div className="container">
                        <section id="consultation-banner" className={`build-future-section`}>
                            {/* Background Subtle Geometric Polygon Mesh / Constellation Lines */}
                            <div className="build-future-bg-mesh" aria-hidden="true">
                                <svg
                                    viewBox="0 0 1440 700"
                                    preserveAspectRatio="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <defs>
                                        <linearGradient id="meshGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                            <stop offset="0%" stopColor="#d97706" stopOpacity="0.12" />
                                            <stop offset="50%" stopColor="#b45309" stopOpacity="0.22" />
                                            <stop offset="100%" stopColor="#ea580c" stopOpacity="0.16" />
                                        </linearGradient>
                                        <filter id="nodeGlow" x="-20%" y="-20%" width="140%" height="140%">
                                            <feGaussianBlur stdDeviation="3" result="blur" />
                                            <feComposite in="SourceGraphic" in2="blur" operator="over" />
                                        </filter>
                                    </defs>

                                    {/* Connected Polygon Wireframe Lines */}
                                    <g stroke="url(#meshGradient)" strokeWidth="1.2" fill="none" opacity="0.8">
                                        <polygon points="120,40 380,90 260,260" />
                                        <polygon points="380,90 620,60 520,240" />
                                        <polygon points="260,260 520,240 430,420" />
                                        <polygon points="520,240 740,190 680,390" />
                                        <polygon points="430,420 680,390 560,580" />
                                        <polygon points="740,190 980,140 920,330" />
                                        <polygon points="680,390 920,330 830,520" />
                                        <polygon points="980,140 1200,90 1140,290" />
                                        <polygon points="920,330 1140,290 1080,480" />
                                        <polygon points="1140,290 1380,240 1320,440" />
                                        <polygon points="1080,480 1320,440 1260,630" />

                                        <line x1="120" y1="40" x2="620" y2="60" strokeOpacity="0.15" />
                                        <line x1="260" y1="260" x2="140" y2="440" />
                                        <line x1="140" y1="440" x2="430" y2="420" />
                                        <line x1="430" y1="420" x2="310" y2="610" />
                                        <line x1="310" y1="610" x2="560" y2="580" />
                                        <line x1="560" y1="580" x2="830" y2="520" />
                                        <line x1="830" y1="520" x2="720" y2="670" />
                                        <line x1="720" y1="670" x2="1080" y2="640" />
                                        <line x1="1080" y1="480" x2="1080" y2="640" />
                                        <line x1="980" y1="140" x2="740" y2="190" strokeOpacity="0.3" />
                                        <line x1="1200" y1="90" x2="1400" y2="60" />
                                        <line x1="1400" y1="60" x2="1380" y2="240" />
                                        <line x1="1380" y1="240" x2="1420" y2="410" />
                                        <line x1="1320" y1="440" x2="1440" y2="580" />
                                    </g>

                                    {/* Glowing Constellation Node Vertices */}
                                    <g fill="#f59e0b" opacity="0.65" filter="url(#nodeGlow)">
                                        <circle cx="380" cy="90" r="3.5" />
                                        <circle cx="520" cy="240" r="3" />
                                        <circle cx="740" cy="190" r="4" />
                                        <circle cx="920" cy="330" r="3.5" />
                                        <circle cx="980" cy="140" r="4" />
                                        <circle cx="1140" cy="290" r="3.5" />
                                        <circle cx="680" cy="390" r="3" />
                                        <circle cx="830" cy="520" r="3.5" />
                                        <circle cx="1080" cy="480" r="4" />
                                        <circle cx="1320" cy="440" r="3" />
                                    </g>
                                </svg>
                            </div>

                            <div className="build-future-container">
                                {/* Left Side: Headline, Copy & Bullets */}
                                <div className="build-future-content">
                                    <span className="build-future-kicker">Lokalt forankret i Herlev</span>
                                    <h2 className="build-future-heading" id="bf-heading-title">
                                        Onlineskolemad i Ballerup – Sådan Støtter vi Lokalt
                                    </h2>

                                    <p className="build-future-text">
                                        Vi arbejder med skoler, institutioner, virksomheder og organisationer overalt i Ballerup. Vores leveringsomfang dækker hele kommunen, og vi har erfaring med at arbejde med forskellige institutionstyper.
                                    </p>
                                    <p className="build-future-text">
                                        <h3>Skoler får Pålidelig Daglig Skolemad</h3>
                                        Grundskoler, private skoler og friskoler i Ballerup kan få leveret fuldt planlagt skolemad. Vi tilpasser os skolens timeplan, antal elever og ønsker til menuer. Rektorer og skolekøkkener kan afgøre, om maden skal tilberedes på stedet eller leveres klar til servering.

                                    </p>
                                    <p className="build-future-text">
                                        <h3>Institutioner får Administrativ Lettelse</h3>
                                        Børnehaver, dagcentre, ældrecentre og andre institutioner kan delegere madordningen til os. Vi håndterer bestilling, levering og tilpasning til brugergruppe.

                                    </p>
                                    <p className="build-future-text">
                                        <h3>Virksomheder giver Medarbejderne en Bedre Dag</h3>
                                        Kontorer og arbejdspladser i Ballerup kan tilbyde daglig frokost uden selv at skulle styre det. Medarbejdere får noget godt at spise, og virksomheden slipper for administrationen.
                                    </p>
                                    <p className="build-future-text">
                                        <h3>Organisationer og Kommunale Tilbud Forenkles</h3>
                                        Foreninger, kommunale enheder og andre organisationer kan få deres forplejning ordnet uden småkræmeri.
                                    </p>
                                    <p className="build-future-text">
                                        <h3>Sådan Starter du</h3>
                                        Du kan kontakte os uanset hvor i Ballerup du er, eller hvilken type organisation du repræsenterer. Vi aftaler detaljer individuelt og skaber en løsning, der passer til dig.
                                    </p>
                                    <div className="herlev-actions">
                                        {/* <Link className="btn btn-1 gap-1" to="/menu">Bestil skolemad <i className="fas fa-arrow-right" /></Link> */}
                                        <Link className="herlev-text-link" to="/contact">Kontakt os for at diskutere dine behov <i className="fas fa-arrow-right" /></Link>
                                    </div>
                                </div>

                                <div className="build-future-image-col">
                                    <div className="build-future-image-wrapper">
                                        <img
                                            src="/assets/img/food-1.png"
                                            alt="Healthy school lunch and meal solutions for Herlev families"
                                            className="build-future-image"
                                            referrerPolicy="no-referrer"
                                            loading="eager"
                                        />
                                    </div>
                                </div>
                            </div>
                        </section >
                    </div>
                </section>

                <section className="herlev-reasons sec-pd">
                    <div className="container">
                        <div className="herlev-reasons-heading">
                            <div>
                                <span className="herlev-eyebrow">Det Gør os til dit Naturlige Valg i Ballerup</span>
                                <h2>Lokal Forståelse, Professionel Service</h2>
                                <p>Vi er ikke bare en online madleverandør – vi forstår Ballerups særlige behov. Vi kender uddannelsesinstitutionerne, virksomhedskulturen og familienes hverdagsrealiteter i kommunen.</p>
                            </div>
                        </div>
                        <div className="herlev-reasons-grid">
                            {reasons.map(([title, text, icon], index) => (
                                <article className="herlev-reason-card" key={title}>
                                    <div className="herlev-reason-number">0{index + 1}</div>
                                    <div className="herlev-reason-icon"><i className={`fas ${icon}`} /></div>
                                    <div>
                                        <h3>{title}</h3>
                                        <p>{text}</p>
                                    </div>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="sec-pd rel-z-1">
                    <div className="top-bottom-shaps">
                        <span className="shap shap-1" />
                        <span className="shap shap-2" />
                    </div>
                    <div className="container">
                        <div className="row align-items-center">
                            <div className="col-md-6 mmb-20">
                                <div className="img-style-1 wow fadeInLeft">
                                    <img src="/assets/img/about-1.png" alt="Madordning i Ballerup klar til levering" loading="lazy" />
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div>
                                    <div
                                        className="title-1 wow fadeInRight"
                                        data-wow-delay="0.1s"
                                        style={{
                                            visibility: "visible",
                                            animationDelay: "0.1s",
                                            animationName: "fadeInUp",
                                        }}
                                    >
                                        <h2 className="title-1-h">Hvem kan bruge Onlineskolemad i Ballerup?</h2>
                                        <div className="herlev-rule"><span /><span /></div>
                                        <p>
                                        </p>
                                        <div className="mt-3">
                                            {/* <h2>Our Vision</h2> */}
                                            <p>
                                                Løsningen er først og fremmest relevant for skoler og skolefamilier, men virksomhedens cateringaktiviteter gør også tilbuddet relevant i andre sammenhænge.
                                            </p>
                                            <p>Det kan eksempelvis være:</p>
                                            <ol>
                                                <li><strong>Skoler</strong>, der ønsker en praktisk løsning til skolemad.</li>
                                                <li><strong>Forældre</strong>, der vil bestille frokost til deres børn.</li>
                                                <li><strong>Institutioner</strong>, hvor der er behov for mad til flere børn eller deltagere.</li>
                                                <li><strong>Virksomheder</strong>, der ønsker mad eller forplejning til medarbejdere eller arrangementer.</li>
                                                <li><strong>Organisationer</strong>, der har brug for mad til et møde eller arrangement.</li>
                                                <li><strong>Arrangører</strong>, der ønsker catering til en aktivitet i Ballerup.</li>
                                            </ol>
                                            <p>Den konkrete løsning afhænger af behov, antal personer og den aftale, der kan etableres.</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="herlev-faq sec-pd">
                    <div className="container">
                        <div className="herlev-section-heading"><span className="herlev-eyebrow">Spørgsmål og svar</span><h2>Spørgsmål og Svar om Skolemad og Madordninger i Ballerup</h2></div>
                        <div className="herlev-faq-list">
                            {BALLERUP_FAQS.map(([question, answer], index) => (
                                <details key={question} open={index === 0}>
                                    <summary>{question}<i className="fas fa-plus" /></summary>
                                    <p>{answer}</p>
                                </details>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="herlev-cta">
                    <div className="container">
                        <div className="herlev-cta-inner"><div>
                            <span className="herlev-eyebrow">Lokalt forankret i Ballerup</span>
                            <h2>Start med Pålidelig Madordning i Dag</h2>
                            <p>Skoler, institutioner, virksomheder og organisationer i Ballerup kan starte med Onlineskolemad. Hvad enten du søger daglig skolemad, frokostordning til kontoret, kantineordning til institutionen eller catering til et arrangement – vi har en løsning.</p>
                            <br />
                            <p>
                                Første skridt er enkelt:
                                <ul style={{ color: '#ffff' }}>
                                    <li style={{ color: '#ffff' }}>Kontakt os med dine behov</li>
                                    <li style={{ color: '#ffff' }}>Vi laver et tilbud der passer til dig</li>
                                    <li style={{ color: '#ffff' }}>Du godkender ordningen og vi starter</li>
                                </ul>
                            </p>
                            <p>Du får pålidelig forplejning, administrativ lettelse og kvalitet, du kan stole på.</p>
                        </div>
                            <div className="herlev-cta-actions">
                                <Link className="btn btn-1" to="/contact">Kontakt Onlineskolemad</Link>
                                <a href="tel:26227469">Telefon: 26 22 74 69</a>
                                <a href="/">Adresse: Herlev Bygade 27, 2730 Herlev</a>
                                <a href="/">E-mail: onlineskolemad@hotmail.com</a>
                            </div>
                        </div>
                    </div>
                </section>
            </main >
        </>
    )
}

export default SkolemadBallerup